import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";
import api from "@/lib/api";
import { track } from "@/lib/track";
import { btnPrimary } from "@/components/kit";
import { ArrowRight, Images } from "lucide-react";

const CATS = ["Tous", "Lancements de produits", "Inaugurations & Boutiques", "Soirées & Galas", "Séminaires & Conventions", "Team Building", "Conférences & Congrès"];

export default function Realisations() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const shown = filter === "Tous" ? projects : projects.filter((p) => p.category === filter);

  return (
    <>
      <Seo title="Nos réalisations" description="Études de cas d'événements d'entreprise organisés par Désir Events : type d'événement, ville, nombre d'invités, prestations réalisées et résultats. Lancements, inaugurations, galas, séminaires." path="/nos-realisations" />
      <PageHero
        eyebrow="Réalisations"
        title={<>Des preuves,<br /><span className="italic text-lime-dim">pas des promesses.</span></>}
        subtitle="Une sélection de projets qui incarnent notre exigence, de la scénographie à l'exécution du jour J."
      />

      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
        <div className="mb-12 flex flex-wrap gap-2" data-testid="realisation-filters">
          {CATS.map((c) => (
            <button key={c} onClick={() => setFilter(c)} className={`rounded-full border px-4 py-2 text-xs uppercase tracking-[0.1em] transition-colors duration-300 ${filter === c ? "border-lime bg-lime text-ink" : "border-ink/15 text-ink/60 hover:border-lime-dim/50 hover:text-ink"}`} data-testid={`filter-${c.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-ink/40">Chargement…</p>
        ) : (
          <div className="columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4">
            {shown.map((p, i) => (
              <Reveal key={p.id} delay={i % 3}>
                <button type="button" onClick={() => setActive(p)} className="group relative block w-full overflow-hidden rounded-2xl border border-ink/10 break-inside-avoid text-left" data-testid={`project-${p.id}`}>
                  <div className={`overflow-hidden ${i % 3 === 0 ? "aspect-[3/4]" : "aspect-[4/3]"}`}>
                    <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  {p.images && p.images.length > 0 && (
                    <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[11px] text-white backdrop-blur-sm">
                      <Images size={12} /> {p.images.length + 1}
                    </span>
                  )}
                  <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/90 via-black/20 to-transparent p-6">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-lime">{p.category}</span>
                    <h3 className="mt-2 font-serif text-2xl font-medium text-white">{p.title}</h3>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-white/70">
                      {p.client && <span>{p.client}</span>}
                      {p.city && <span>· {p.city}</span>}
                      {p.year && <span>· {p.year}</span>}
                      {p.guests && <span>· {p.guests}</span>}
                    </div>
                    <p className="mt-3 max-h-0 overflow-hidden text-sm text-white/80 opacity-0 transition-all duration-500 group-hover:max-h-32 group-hover:opacity-100">{p.description}</p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}

        {!loading && shown.length === 0 && <p className="text-ink/40">Aucune réalisation dans cette catégorie pour le moment.</p>}

        <Reveal delay={1}>
          <div className="mt-20 text-center">
            <p className="font-serif text-3xl text-ink md:text-5xl">Et si le prochain projet était le vôtre ?</p>
            <Link to="/devis" onClick={() => track("click_devis")} className={`${btnPrimary} mt-8`}>Recevoir une première proposition <ArrowRight size={16} /></Link>
          </div>
        </Reveal>
      </section>

      <Lightbox project={active} onClose={() => setActive(null)} />
    </>
  );
}
